import React, { useEffect, useState, useRef, useCallback } from "react";
import { ClipLoader } from "react-spinners";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { Link } from "react-router-dom";
import { FaMapMarkedAlt, FaImages, FaBlog, FaCalendarAlt, FaClipboardList, FaInfoCircle } from "react-icons/fa";
import { FaSuitcase, FaGlobe, FaHotel, FaCalendarCheck, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import Header, { NavLink, LogoLink, NavToggle, DesktopNavLinks } from "./light.js";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV;

const StyledHeader = styled(Header)`
  ${tw`pt-4 max-w-none w-full`}
  ${DesktopNavLinks} ${NavLink}, ${LogoLink} {
    ${tw`text-white hover:border-white`} 
    font-family: 'Roboto', sans-serif !important;
  }
  ${NavToggle}.closed {
    ${tw`text-white hover:text-[#0049AC]`}
  }

  ${LogoLink} {
    ${tw`text-xl md:text-3xl flex items-center`};
  }

  ${({ isSticky }) => isSticky && css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    background-color: #0049AC;
    transition: top 0.3s ease, background-color 0.3s ease;
    padding: 8px;
  `}

  ${LogoLink} img {
    ${({ isSticky }) => isSticky && tw`hidden`}
  }

  ${LogoLink} span {
    display: none;
    ${({ isSticky }) => isSticky && tw`block text-white font-bold`}
  }
`;

const StyledNavLink = styled(NavLink)`
  ${tw`relative flex flex-col items-center text-white font-bold antialiased py-3 px-4`};
  font-size: 14px;
  font-family: 'Roboto', sans-serif !important;
  transition: all 0.3s ease-in-out;
  border-bottom: 2px solid transparent;
  &:hover, &.hover-active {
    font-size: 14px;
    transform: scale(1.1);
    border-bottom: 2px solid white;
  }
`;

const IconWrapper = styled.div`
  ${tw`mb-1 text-lg w-6 h-6`};
`;

const NavContainer = styled.div`
  ${tw`flex flex-col md:flex-row items-center justify-center w-full`}
`;

const Container = styled.div`
  ${tw`relative -mx-8 -mt-8 bg-center bg-cover h-screen min-h-144`}
`;

const VideoContainer = styled.div`
  ${tw`absolute top-0 left-0 w-full h-full`};
  z-index: -1;
`;

const VideoBackground = styled.video`
  ${tw`w-full h-full`};
  object-fit: cover;
  opacity: 0.98;
`;

const MuteButton = styled.button`
  ${tw`absolute p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 transition-all`}
  bottom: 16px;
  right: 16px;
  z-index: 20;
  pointer-events: auto;
  @media (max-width: 767px) {
    bottom: 8px;
    right: 8px;
    padding: 4px;
  }
`;

const HeroContainer = tw.div`z-20 relative px-2 sm:px-4 mx-auto h-full flex flex-col`;
const Content = tw.div`px-4 flex flex-1 flex-col justify-center items-center`;

const StatsGrid = styled.div`
  ${tw`absolute bg-white bg-opacity-25 p-4 rounded-lg grid grid-cols-2 gap-8 shadow-lg`}
  border: 1px solid rgba(0, 0, 0, 0.2);
  z-index: 5;

  @media (min-width: 768px) {
    ${tw`bottom-0 right-0 mr-24 mb-24`}

    &:hover {
      font-size: 16px;
      transform: scale(1.2);
    }
    font-family: 'Roboto', sans-serif !important
  }

  @media (max-width: 767px) {
    ${tw`bottom-0 mb-20 w-11/12`}
    font-family: 'Roboto', sans-serif !important
  }
`;

const StatCard = styled.div`
  ${tw`flex flex-col items-center p-2`}
  border-right: 1px solid rgba(0, 0, 0, 0.2);

  &:nth-child(2n) {
    border-right: none;
  }

  &:nth-last-child(-n+2) {
    border-bottom: none;
  }
`;

const StatIcon = styled.div`
  ${tw`text-[#0049AC] text-2xl`}
`;

const StatValue = styled.div`
  ${tw`text-lg md:text-xl text-white font-bold`}
`;

const StatLabel = styled.div`
  ${tw`text-sm text-white`}
`;

const statIcons = {
  "Tour Packages": <FaSuitcase />,
  "Destinations": <FaGlobe />,
  "Hotel & Resorts": <FaHotel />,
  "Events & Activities": <FaCalendarCheck />,
};

const ModalContent = styled.div`
  ${tw`relative flex flex-col justify-start bg-white shadow-lg p-6 rounded-lg mr-20 ml-20 mb-20 h-3/4 w-3/4 z-20 pointer-events-auto`}
  top: 78px;
  left: 0;
  right: 0;
  overflow: hidden;
  font-family: 'Roboto', sans-serif !important;

  & > div {
    display: flex;
    white-space: nowrap;
    gap: 32px;
  }
`;

const Modal = tw.div`
  fixed z-20 inset-0 flex items-center justify-center 
  transition-transform duration-300 ease-out
  animate-[fadeIn_0.3s_ease-out_forwards]
  pointer-events-none
`;

const DropdownToggle = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  margin-left: 10px;
`;

const DropdownContent = styled.div`
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  background: #0049AC;
  padding: 10px;
  width: 100%;
  height: auto;
  overflow: hidden;
  font-family: 'Roboto', sans-serif !important;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SubmenuTitle = styled.div`
  font-weight: bold;
  margin-top: 10px;
`;

const fallbackMenuData = {
  destinations: {
    title: "Explore Destinations",
    submenus: [
      {
        title: "Domestic Destinations",
        menus: [
          { name: "India", link: "pg/destinations/explore/india" },
          { name: "Thailand", link: "pg/destinations/explore/thailand" },
          { name: "Japan", link: "pg/destinations/explore/japan" },
          { name: "India", link: "pg/destinations/explore/india" },
          { name: "Thailand", link: "pg/destinations/explore/thailand" },
          { name: "Japan", link: "pg/destinations/explore/japan" },
          { name: "India", link: "pg/destinations/explore/india" },
          { name: "Thailand", link: "pg/destinations/explore/thailand" },
          { name: "Japan", link: "pg/destinations/explore/japan" },
          { name: "India", link: "pg/destinations/explore/india" },
          { name: "Thailand", link: "pg/destinations/explore/thailand" },
          { name: "Japan", link: "pg/destinations/explore/japan" },
          { name: "India", link: "pg/destinations/explore/india" },
          { name: "Thailand", link: "pg/destinations/explore/thailand" },
        ]
      },
      {
        title: "International Destinations",
        menus: [
          { name: "Italy", link: "pg/destinations/explore/italy" },
          { name: "France", link: "pg/destinations/explore/france" },
          { name: "Germany", link: "pg/destinations/explore/germany" },
          { name: "Italy", link: "pg/destinations/explore/italy" },
          { name: "France", link: "pg/destinations/explore/france" },
          { name: "Germany", link: "pg/destinations/explore/germany" },
          { name: "Italy", link: "pg/destinations/explore/italy" },
          { name: "France", link: "pg/destinations/explore/france" },
          { name: "Germany", link: "pg/destinations/explore/germany" },
          { name: "Italy", link: "pg/destinations/explore/italy" },
          { name: "France", link: "pg/destinations/explore/france" },
          { name: "Germany", link: "pg/destinations/explore/germany" },
          { name: "Italy", link: "pg/destinations/explore/italy" }
        ]
      },
      {
        title: "Special Destinations",
        menus: [
          { name: "Germany", link: "pg/destinations/explore/germany" },
          { name: "Italy", link: "pg/destinations/explore/italy" },
          { name: "France", link: "pg/destinations/explore/france" },
          { name: "Germany", link: "pg/destinations/explore/germany" },
          { name: "Italy", link: "pg/destinations/explore/italy" },
          { name: "France", link: "pg/destinations/explore/france" },
          { name: "Germany", link: "pg/destinations/explore/germany" },
          { name: "Italy", link: "pg/destinations/explore/italy" }
        ]
      },
    ]
  },
  checklists: {
    title: "Visa Checklist",
    submenus: [
      {
        title: "Asia Visa Checklist",
        menus: [
          { name: "Australia", link: "/visa/business/australia" },
          { name: "Canada", link: "/visa/business/canada" },
          { name: "Australia", link: "/visa/business/australia" },
          { name: "Canada", link: "/visa/business/canada" }
        ]
      },
      {
        title: "Europe Visa Checklist",
        menus: [
          { name: "UK", link: "/visa/tourist/uk" },
          { name: "USA", link: "/visa/tourist/usa" },
          { name: "UK", link: "/visa/tourist/uk" },
          { name: "USA", link: "/visa/tourist/usa" }
        ]
      }
    ]
  }
};

export default () => {
  const [data, setData] = useState({
    statistics: [
      { key: "Tour Packages", value: "192" },
      { key: "Destinations", value: "479" },
      { key: "Hotel & Resorts", value: "2093" },
      { key: "Events & Activities", value: "256" },
    ],
  });

  const [isSticky, setIsSticky] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const [submenuData, setSubmenuData] = useState(fallbackMenuData);
  const [activeMenu, setActiveMenu] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [openMenu, setOpenMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);

  const toggleDropdown = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const toggleMute = () => {
    if (videoRef.current && videoReady && hasAudio) {
      console.log('Toggle mute clicked, current muted state:', videoRef.current.muted);
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
      console.log('New muted state:', videoRef.current.muted);
    } else {
      console.log('Video not ready, no audio, or ref not available');
    }
  };

  const hasSubmenu = (menu) => ["destinations", "checklists"].includes(menu);

  const navLinks = [
    <NavContainer key={1}>
      <StyledNavLink as={Link}
        to="/pg/destinations"
        onMouseEnter={() => handleMouseEnter("destinations")}
        onMouseLeave={(event) => handleMouseGoneMenu("destinations", event)}
        className={`${activeMenu === "destinations" && !isMobile ? "hover-active" : ""}`}>
        <IconWrapper><FaMapMarkedAlt /></IconWrapper>
        EXPLORE DESTINATIONS
      </StyledNavLink>
      {isMobile && (
        <DropdownToggle onClick={() => toggleDropdown("destinations")}>
          <span style={{ marginLeft: "-12px", display: "flex", alignItems: "center" }}>
            {openMenu === "destinations" ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </span>
          <br />
        </DropdownToggle>
      )}
      {isMobile && openMenu === "destinations" && (
        <DropdownContent isOpen={openMenu === "destinations"}>
          {submenuData.destinations.submenus
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((submenu, index) => (
              <div key={index}>
                <SubmenuTitle>{submenu.title}</SubmenuTitle>
                {submenu.menus.map((item, i) => (
                  <Link key={i} to={openMenu === "checklists" ? item.link : `/pg/${item.link || ''}`} style={{ display: "block", margin: "5px 0" }}>
                    {item.name}
                  </Link>
                ))}
              </div>
            ))}
        </DropdownContent>
      )}
      <StyledNavLink as={Link} to="/pg/gallery" >
        <IconWrapper><FaImages /></IconWrapper>
        GALLERY
      </StyledNavLink>
      <StyledNavLink as={Link} to="/pg/blogs" >
        <IconWrapper><FaBlog /></IconWrapper>
        BLOGS
      </StyledNavLink>
      <StyledNavLink as={Link} to="/pg/events" >
        <IconWrapper><FaCalendarAlt /></IconWrapper>
        EVENTS & ACTIVITIES
      </StyledNavLink>
      <StyledNavLink as={Link}
        to="/pg/visa-checklist"
        onMouseEnter={() => handleMouseEnter("checklists")}
        onMouseLeave={(event) => handleMouseGoneMenu("checklists", event)}
        className={`${activeMenu === "checklists" && !isMobile ? "hover-active" : ""}`}>
        <IconWrapper><FaClipboardList /></IconWrapper>
        VISA CHECKLIST
      </StyledNavLink>
      {isMobile && (
        <DropdownToggle onClick={() => toggleDropdown("checklists")}>
          <span style={{ marginLeft: "-12px", display: "flex", alignItems: "center" }}>
            {openMenu === "checklists" ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </span>
          <br />
        </DropdownToggle>
      )}
      {isMobile && openMenu === "checklists" && (
        <DropdownContent isOpen={openMenu === "checklists"}>
          {submenuData.checklists.submenus
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((submenu, index) => (
              <div key={index}>
                <SubmenuTitle>{submenu.title}</SubmenuTitle>
                {submenu.menus
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((item, i) => (
                    <Link key={i} to={item.link} style={{ display: "block", margin: "5px 0" }}>
                      {item.name}
                    </Link>
                  ))}
              </div>
            ))}
        </DropdownContent>
      )}
      <StyledNavLink as={Link} to="/pg/know-us" >
        <IconWrapper><FaInfoCircle /></IconWrapper>
        KNOW US
      </StyledNavLink>
    </NavContainer>
  ];

  useEffect(() => {
    
    const fetchHomeStats = async () => {
      try {
        const response = await fetch(`${API_URL}/web/home/home-stats`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
          }
        });
        const result = await response.json();
        if (result.header.resCode == 200 && result.data.length > 0) {
          setData({
            statistics: result.data[0].statistics.length > 0 ? result.data[0].statistics : data.statistics,
          });
        }
      } catch (error) {
        console.error("Failed to fetch data, using default values.", error);
      }
    };

    const fetchSubmenuData = async () => {
      try {
        const response = await fetch(`${API_URL}/web/home/menu`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
          }
        });
        const result = await response.json();
        if (result.header.resCode === 200 && result.data.length > 0) {
          const sortedDestinations = {
            ...result.data[0],
            destinations: {
              ...result.data[0].destinations,
              submenus: result.data[0].destinations.submenus
                .map(submenu => ({
                  ...submenu,
                  menus: submenu.menus.sort((a, b) => a.name.localeCompare(b.name))
                }))
                .sort((a, b) => a.title.localeCompare(b.title))
            },
            checklists: {
              ...result.data[0].checklists,
              submenus: result.data[0].checklists.submenus
                .map(submenu => ({
                  ...submenu,
                  menus: submenu.menus.sort((a, b) => a.name.localeCompare(b.name))
                }))
                .sort((a, b) => a.title.localeCompare(b.title))
            }
          };
          setSubmenuData(sortedDestinations);
        }
      } catch (error) {
        console.error("Error fetching submenu data:", error);
      }
    };
    fetchHomeStats();
    fetchSubmenuData();
  }, []);

  useEffect(() => {
  const fetchVideoUrl = async () => {
    try {
      const response = await fetch(`${API_URL}/web/home/bgvideo?title=homeVideoUrl`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
        }
      });
      const result = await response.json();
      if (result.header.resCode === 200 && result.data.length > 0) {
        const videoData = result.data.find((item) => item.title === "homeVideoUrl");
        setVideoUrl(videoData?.content || ''); // Ensure videoUrl is always a string
      }
    } catch (error) {
      console.error('Error fetching video URL:', error);
      setVideoUrl(''); // Fallback to empty string
    }
  };

  const fetchMobileVideoUrl = async () => {
    try {
      const response = await fetch(`${API_URL}/web/home/bgvideo?title=mobileVideoUrl`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY
        }
      });
      const result = await response.json();
      if (result.header.resCode === 200 && result.data.length > 0) {
        const videoData = result.data.find((item) => item.title === "mobileVideoUrl");
        if (videoData && isMobile) {
          setVideoUrl(videoData.content || '');
        }
      }
    } catch (error) {
      console.error('Error fetching mobile video URL:', error);
      setVideoUrl(''); // Fallback to empty string
    }
  };

  fetchVideoUrl();
  fetchMobileVideoUrl();
}, [isMobile]);

  useEffect(() => {
    if (videoRef.current) {
      const checkVideo = () => {
        if (videoRef.current && videoRef.current.readyState >= 2) {
          console.log('Video loaded, audio tracks:', videoRef.current.audioTracks);
          setVideoReady(true);
          setLoading(false);
          if (videoRef.current.audioTracks && videoRef.current.audioTracks.length > 0) {
            setHasAudio(true);
          } else {
            setHasAudio(false);
            console.log('No audio tracks detected in the video');
          }
          document.body.style.overflow = 'auto';
        }
      };
      videoRef.current.addEventListener('loadeddata', checkVideo);
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadeddata', checkVideo);
        }
      };
    }
  }, [videoUrl]);

  const handleScroll = useCallback(() => {
    if (document.querySelector(".hero-container") && window.pageYOffset > document.querySelector(".hero-container").offsetHeight) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  }, []);

  const handleMouseEnter = (menu) => {
    if (hasSubmenu(menu)) {
      if (!isMobile && !modalVisible) {
        setActiveMenu(menu);
        setModalVisible(true);
        document.body.style.overflow = 'hidden';
      }
    }
  };

  const handleMouseGoneMenu = (menu, event) => {
    if (event.relatedTarget && event.clientY < event.currentTarget.getBoundingClientRect().bottom) {
      setActiveMenu(null);
      setModalVisible(false);
      document.body.style.overflow = 'auto';
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setModalVisible(false);
      setActiveMenu(null);
      document.body.style.overflow = 'auto';
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  return (
    <>
      <Container>
        {loading &&
          <div style={styles.loaderContainer}>
            <ClipLoader size={60} color="#0049AC" />
            <p>Traviator Loading...</p>
          </div>
        }
        {videoUrl && (
          <VideoContainer>
            <VideoBackground
              ref={videoRef}
              src={videoUrl}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              preload="auto"
            />
            {videoReady && hasAudio && (
              <MuteButton onClick={toggleMute}>
                {isMuted ? <FaVolumeMute size={24} /> : <FaVolumeUp size={24} />}
              </MuteButton>
            )}
          </VideoContainer>
        )}
        <HeroContainer className="hero-container">
          <div className="hero-container relative z-20 px-2 sm:px-4 mx-auto h-full flex flex-col">
            <StyledHeader links={navLinks} isSticky={isSticky} />
          </div>
          <Content>
            <StatsGrid>
              {data.statistics.map((stat, index) => (
                <StatCard key={index}>
                  <StatIcon>{statIcons[stat.key] || <FaInfoCircle />}</StatIcon>
                  <StatValue>{stat.value}</StatValue>
                  <StatLabel>{stat.key}</StatLabel>
                </StatCard>
              ))}
            </StatsGrid>
          </Content>
        </HeroContainer>
      </Container>
      {
        modalVisible && !isMobile && (
          <Modal>
            <ModalContent
              onMouseLeave={handleMouseLeave}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: "flex",
                gap: "8px",
                padding: "4px",
              }}>
                {submenuData[activeMenu]?.submenus
                  ?.sort((a, b) => a.title.localeCompare(b.title))
                  .map((item, index) => (
                    <div
                      key={index}
                      style={{
                        width: "300px",
                        padding: "8px",
                        position: "relative",
                        borderRadius: "2px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: "900",
                          marginBottom: "4px",
                          position: "sticky",
                          top: "0",
                          padding: "4px",
                          zIndex: "10"
                        }}
                      >
                        {item.title}
                      </h3>
                      <div
                        style={{
                          height: "400px",
                        }}
                      >
                        <ul style={{ listStyleType: "none", padding: "0", margin: "0" }}>
                          {item.menus
                            ?.sort((a, b) => a.name.localeCompare(b.name))
                            .map((subItem, subIndex) => (
                              <li
                                key={subIndex}
                                style={{
                                  padding: "1px",
                                  marginLeft: "4px",
                                  backgroundColor: "white",
                                  transition: "transform 0.2s, color 0.2s, background-color 0.2s",
                                  cursor: "pointer"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = "#0049AC";
                                  e.currentTarget.style.transform = "scale(1.05)";
                                  e.currentTarget.style.backgroundColor = "#f0f8ff";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = "#000";
                                  e.currentTarget.style.transform = "scale(1)";
                                  e.currentTarget.style.backgroundColor = "white";
                                }}
                              >
                                <Link
                                  to={activeMenu === "checklists" ? subItem.link : `/pg/${subItem.link || ''}`}
                                  target={activeMenu === "checklists" ? "_blank" : "_self"}
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    padding: "4px",
                                  }}
                                >
                                  {subItem.name}
                                </Link>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  ))}
              </div>
            </ModalContent>
          </Modal>
        )
      }
    </>
  );
};

const styles = {
  loaderContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#fff",
  },
};